import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environment/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

enum FastAPIAddressesSource {
  Nominatim = 'nominatim',
  GetCovered = 'getcovered',
  OSM = 'osm',
  Google = 'google',
  None = 'None'
}

const FAST_API_NON_GOOGLE_SOURCES: FastAPIAddressesSource[] = [
  FastAPIAddressesSource.Nominatim,
  FastAPIAddressesSource.GetCovered,
  FastAPIAddressesSource.OSM
];

interface FastAPIAddress {
  source: FastAPIAddressesSource.Nominatim | FastAPIAddressesSource.GetCovered | FastAPIAddressesSource.Google;
  street_number?: string;
  street_name?: string;
  city?: string;
  county?: string;
  state?: string;
  state_code?: number;
  zip_code?: string;
  country?: string;
  full_str: string;
  place_id?: string;
  neighborhood?: string;
}

interface FastAPIResponse {
  data: FastAPIAddress[];
}

interface FastAPIAddressLegacy {
  city?: string;
  country?: string;
  county?: string;
  neighborhood?: string;
  full_str: string;
  lat?: number;
  lon?: number;
  state?: string;
  state_code?: number;
  street_name?: string;
  street_number?: string;
  zip_code?: string;
}

interface FastAPIAddressExtended extends FastAPIAddressLegacy {
  address: string;
}

interface GoogleAPIAddress {
  description: string;
  place_id: string;
}

interface FastAPIResponseLegacy {
  gc?: unknown[];
  google?: GoogleAPIAddress[];
  osm?: FastAPIAddressLegacy[];
}

@Injectable({
  providedIn: 'root'
})
class FastAPIService {
  constructor(private httpClient: HttpClient) {}

  lookup(searchString: string): Observable<FastAPIResponse | FastAPIResponseLegacy> {
    return this.httpClient.get<FastAPIResponse | FastAPIResponseLegacy>(
      `${environment.addressesSearch.fastAPIBase}/lookup?q=${encodeURIComponent(searchString)}`
    );
  }

  sendPlace(id: string): Observable<FastAPIAddress | FastAPIAddressExtended> {
    return this.httpClient
      .get<FastAPIAddress | FastAPIAddressLegacy>(
        `${environment.addressesSearch.fastAPIBase}/place_details?place_id=${encodeURIComponent(id)}`
      )
      .pipe(
        map(res => {
          if ('address' in res) {
            return res as FastAPIAddressExtended;
          }
          return { ...res, address: res.full_str } as FastAPIAddressExtended;
        })
      );
  }
}

export {
  FastAPIResponse,
  FastAPIResponseLegacy,
  FastAPIAddress,
  FastAPIAddressLegacy,
  FastAPIAddressExtended,
  GoogleAPIAddress,
  FastAPIAddressesSource,
  FAST_API_NON_GOOGLE_SOURCES,
  FastAPIService
};
